import { ConfigStateService, LocalizationService } from '@abp/ng.core';
import { ToasterService } from '@abp/ng.theme.shared';
import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfigurationSettingsDto } from 'src/core/models/configuration-setting/configuration-settings.dto';
import { ConfigurationSettingsService } from 'src/core/services/configuration-settings/configuration-settings.service';
import { SettingConstants } from 'src/core/constants/setting-constant';

type RestrictedControlName = 'genderEnabled' | 'emotionEnabled' | 'sentimentEnabled' | 'acousticEnabled';

@Component({
  selector: 'ca-analytics-features-settings',
  templateUrl: './analytics-features-settings.component.html',
  styleUrls: ['./analytics-features-settings.component.scss'],
  standalone: false,
})
export class AnalyticsFeaturesSettingsComponent implements OnInit {
  form: FormGroup;

  @ViewChild('restrictedFeatureConfirmModal')
  restrictedFeatureConfirmModal: TemplateRef<unknown>;

  confirmModalChecked = false;

  pendingRestrictedFeatures: { controlName: RestrictedControlName; labelKey: string }[] = [];

  pendingRestrictedFeatureNamesLabel = '';

  private readonly restrictedControlsLabelKeys: Record<RestrictedControlName, string> = {
    genderEnabled: 'Settings::AnalyticsFeatures:Gender.Enabled',
    emotionEnabled: 'Settings::AnalyticsFeatures:Emotion.Enabled',
    sentimentEnabled: 'Settings::AnalyticsFeatures:Sentiment.Enabled',
    acousticEnabled: 'Settings::AnalyticsFeatures:Acoustic.Enabled',
  };

  private initialRestrictedValues: Record<RestrictedControlName, boolean>;

  constructor(
    private config: ConfigStateService,
    private fb: FormBuilder,
    private settingService: ConfigurationSettingsService,
    private toastr: ToasterService,
    private localizationService: LocalizationService,
    private modalService: NgbModal,
  ) {
    this.form = fb.group({
      genderEnabled: [
        JSON.parse(this.config.getSetting(SettingConstants.GenderEnabled).toLowerCase()),
      ],
      emotionEnabled: [
        JSON.parse(this.config.getSetting(SettingConstants.EmotionEnabled).toLowerCase()),
      ],
      sentimentEnabled: [
        JSON.parse(this.config.getSetting(SettingConstants.SentimentEnabled).toLowerCase()),
      ],
      textCorrectionEnabled: [
        JSON.parse(this.config.getSetting(SettingConstants.TextCorrectionEnabled).toLowerCase()),
      ],
      acousticEnabled: [
        JSON.parse(this.config.getSetting(SettingConstants.AcousticEnabled).toLowerCase()),
      ],
    });
    this.form
      .get('textCorrectionEnabled')
      .patchValue(
        JSON.parse(this.config.getSetting(SettingConstants.TextCorrectionEnabled).toLowerCase()),
      );
    this.form
      .get('acousticEnabled')
      .patchValue(
        JSON.parse(this.config.getSetting(SettingConstants.AcousticEnabled).toLowerCase()),
      );
    this.form
      .get('emotionEnabled')
      .patchValue(JSON.parse(this.config.getSetting(SettingConstants.EmotionEnabled).toLowerCase()));
    this.form
      .get('sentimentEnabled')
      .patchValue(
        JSON.parse(this.config.getSetting(SettingConstants.SentimentEnabled).toLowerCase()),
      );
    this.form
      .get('genderEnabled')
      .patchValue(JSON.parse(this.config.getSetting(SettingConstants.GenderEnabled).toLowerCase()));

    this.initialRestrictedValues = this.getCurrentRestrictedValues();
  }

  ngOnInit(): void {}

  onSubmitForm() {
    if (!this.form.valid) {
      return;
    }

    const newlyEnabledRestrictedFeatures = this.getNewlyEnabledRestrictedFeatures();

    if (newlyEnabledRestrictedFeatures.length === 0) {
      this.persistSettings();
      return;
    }

    this.openRestrictedFeatureConfirmModal(newlyEnabledRestrictedFeatures);
  }

  onConfirmCheckboxChange(event: Event) {
    this.confirmModalChecked = (event.target as HTMLInputElement).checked;
  }

  private getCurrentRestrictedValues(): Record<RestrictedControlName, boolean> {
    return {
      genderEnabled: this.form.get('genderEnabled').value,
      emotionEnabled: this.form.get('emotionEnabled').value,
      sentimentEnabled: this.form.get('sentimentEnabled').value,
      acousticEnabled: this.form.get('acousticEnabled').value,
    };
  }

  private getNewlyEnabledRestrictedFeatures(): {
    controlName: RestrictedControlName;
    labelKey: string;
  }[] {
    return (Object.keys(this.restrictedControlsLabelKeys) as RestrictedControlName[])
      .filter(
        controlName =>
          this.initialRestrictedValues[controlName] === false &&
          this.form.get(controlName).value === true,
      )
      .map(controlName => ({
        controlName,
        labelKey: this.restrictedControlsLabelKeys[controlName],
      }));
  }

  private openRestrictedFeatureConfirmModal(
    newlyEnabledRestrictedFeatures: { controlName: RestrictedControlName; labelKey: string }[],
  ) {
    this.pendingRestrictedFeatures = newlyEnabledRestrictedFeatures;
    this.pendingRestrictedFeatureNamesLabel = newlyEnabledRestrictedFeatures
      .map(feature => this.localizationService.instant(feature.labelKey))
      .join(', ');
    this.confirmModalChecked = false;

    const modalRef = this.modalService.open(this.restrictedFeatureConfirmModal, {
      centered: true,
      backdrop: 'static',
      keyboard: false,
    });

    modalRef.result.then(
      (result: string) => {
        if (result === 'confirm') {
          this.persistSettings();
        }
        this.resetRestrictedFeatureConfirmState();
      },
      () => {
        this.pendingRestrictedFeatures.forEach(feature => {
          this.form.get(feature.controlName).patchValue(false);
        });
        this.resetRestrictedFeatureConfirmState();
      },
    );
  }

  private resetRestrictedFeatureConfirmState() {
    this.confirmModalChecked = false;
    this.pendingRestrictedFeatures = [];
    this.pendingRestrictedFeatureNamesLabel = '';
  }

  private persistSettings() {
    const settings: ConfigurationSettingsDto[] = [];

    settings.push(
      {
        settingName: SettingConstants.EmotionEnabled,
        settingValue: this.form.get('emotionEnabled').value.toString(),
      },
      {
        settingName: SettingConstants.SentimentEnabled,
        settingValue: this.form.get('sentimentEnabled').value.toString(),
      },
      {
        settingName: SettingConstants.TextCorrectionEnabled,
        settingValue: this.form.get('textCorrectionEnabled').value.toString(),
      },
      {
        settingName: SettingConstants.GenderEnabled,
        settingValue: this.form.get('genderEnabled').value.toString(),
      },
      {
        settingName: SettingConstants.AcousticEnabled,
        settingValue: this.form.get('acousticEnabled').value.toString(),
      },
    );

    this.settingService.saveSetting(settings).subscribe(() => {
      this.toastr.success(
        this.localizationService.instant('AbpSettingManagement::SavedSuccessfully'),
      );
      this.initialRestrictedValues = this.getCurrentRestrictedValues();
    });
  }
}
